import { useBreakpoints } from '@/Utility/hooks';
import { useAppStore } from '@/inertia/AppStore';
import {
  FrontendEmailSharing,
  GridView,
  Player,
  QrCodeSharing,
  SideBar,
  shareButtonStyles,
} from '@/inertia/Shared/MainContainer';
import { Grid, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { PAGE_LAYOUT } from '../../Page';

import './LandingPage.template.less';
import SearchBar from '../../TopBar/SearchBar';

/**
 * General View Template: Landing Page
 * This template shows the user profile at the top of the page
 */

type Props = {
  title?: string;
  profileImage?: {
    src?: string | null;
    alt?: string | null;
  };
  textArea?: {
    name?: string | null;
    role?: string | null;
    description?: string | null;
  };
  socialIcons?: React.JSX.Element[];
  articleLinks: React.JSX.Element[];
};

const LandingPageDesktop = (props: Props) => {
  const barView = useAppStore((state) => state.barView);
  const descriptionRef = useRef<HTMLSpanElement>(null);

  // @todo: remove this feature flag
  const isSearchBarEnabled = useMemo(() => {
    return window.location.origin.includes('exlservice.com') === false;
  }, []);

  const { topStyle, shareGridStyles } = useMemo(() => {
    const shareMetaLength = (props?.socialIcons?.length ?? 0) + 2; // +2 for email and qr code
    const columns = shareMetaLength % 2 === 0 ? shareMetaLength / 2 : Math.floor(shareMetaLength / 2) + 1;

    return {
      topStyle: (props?.articleLinks?.length ?? 0) <= 1 ? 'straight' : 'curved',
      shareGridStyles: {
        display: 'grid',
        gridTemplateRows: 'repeat(2, 1fr)',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
      } as React.CSSProperties,
    };
  }, [props?.articleLinks]);

  useEffect(() => {
    const descriptionElement = descriptionRef.current;
    if (!descriptionElement) return;

    const descriptionText = descriptionElement.textContent;

    if (descriptionText && descriptionText.length > 100) {
      // adjust the number based on your needs
      descriptionElement.style.display = '-webkit-box';
      descriptionElement.style.webkitLineClamp = '3';
      descriptionElement.style.webkitBoxOrient = 'vertical';
    } else {
      descriptionElement.style.display = 'flex';
      descriptionElement.style.justifyContent = 'center';
      descriptionElement.style.alignItems = 'center';
    }
  }, [props?.textArea?.description]);

  return (
    <div className="landing-page-template desktop">
      <div className={'floating-container'}>
        <div className="item-1 profile">
          <Typography className="subject" variant="body1" fontWeight={500} lineHeight={1.2}>
            {props.title}
          </Typography>
          <img
            className="image"
            src={props?.profileImage?.src ?? ''}
            alt={`Profile Image: ${props.profileImage?.alt} `}
          />
          <div className="text-area">
            <Typography className="name" variant="body1" lineHeight={1.2}>
              {props?.textArea?.name ?? ''}
            </Typography>
            <Typography className="role" variant="body2" lineHeight={1.2} fontStyle={'italic'}>
              {props?.textArea?.role ?? ''}
            </Typography>
          </div>
          <Typography
            ref={descriptionRef}
            className="description"
            data-description={props?.textArea?.description ?? ''}
            variant="body1"
            lineHeight={1.2}
            title={props?.textArea?.description ?? ''}
          >
            {props?.textArea?.description ?? ''}
          </Typography>
          <div style={{ height: '100%', ...shareGridStyles }}>
            <FrontendEmailSharing buttonClassName={shareButtonStyles.socialBtn} />
            <QrCodeSharing buttonClassName={shareButtonStyles.socialBtn} />
            {props?.socialIcons}
          </div>
        </div>
        <div className="item-2" /> {/* connector top */}
        <div className={`item-3 ${topStyle}`} /> {/* connector bottom */}
        <div className={`item-4 article-links ${topStyle}`}>{props?.articleLinks}</div>
        <div className={`item-5 ${topStyle} z-${barView === 'list' ? 'show' : 'hide'}`}>
          <Player />
        </div>
        {/* @todo: remove this feature flag */}
        {isSearchBarEnabled && (
          <div className={`search-bar ${topStyle} z-${barView === 'list' ? 'show' : 'hide'}`}>
            <SearchBar />
          </div>
        )}
        <div className={`item-6 ${topStyle} z-${barView === 'list' ? 'show' : 'hide'}`}>
          <SideBar />
        </div>
        <div className={`item-7 ${topStyle} z-${barView === 'grid' ? 'show' : 'hide'}`}>
          <GridView showShare={false} />
        </div>
      </div>
    </div>
  );
};

const LandingPageMobile = (props: Props) => {
  return (
    <Stack className="landing-page-template mobile">
      <div>
        <h6 className="subject">{props.title}</h6>
      </div>
      <Stack direction="row">
        <div>
          <img
            className="image"
            src={props?.profileImage?.src ?? ''}
            alt={`Profile Image: ${props.profileImage?.alt} `}
          />
        </div>
        <div>
          <div className="name">{props.textArea?.name}</div>
          <div className="role">{props.textArea?.role}</div>
        </div>
      </Stack>
      <div className="description">{props.textArea?.description}</div>
      <div className={shareButtonStyles.socialBtnContainer}>
        <FrontendEmailSharing buttonClassName={shareButtonStyles.socialBtn} />
        <QrCodeSharing buttonClassName={shareButtonStyles.socialBtn} />
        {props?.socialIcons}
      </div>
      <div className="article-links">{props?.articleLinks}</div>
    </Stack>
  );
};

export const LandingPageTemplate = (props: Props) => {
  const setShowTopBar = useAppStore((state) => state.setShowTopBar);
  const { isMobile } = useBreakpoints();

  useEffect(() => {
    setShowTopBar(false);

    return () => {
      setShowTopBar(true);
    };
  }, []);

  if (isMobile) {
    return (
      <Grid container sx={{ mt: 0.5, mb: 1 }}>
        <Grid size={{ xs: 12 }}>
          <Player />
          <LandingPageMobile {...props} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <SideBar />
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container mt={0.5} {...PAGE_LAYOUT.centerProps}>
      <LandingPageDesktop {...props} />
    </Grid>
  );
};

export default LandingPageTemplate;
